import { type ElementSource } from '../element.ts';

/**
 * A reference to another CollaborationTool on the Canvas board.
 */
export type CollaborationToolId = string & { __collaborationToolId: symbol };

export const COLLAB_TOOL_ID_PREFIX = 'collaboration-tool-';

export const collaborationToolId = (id: string) => {
  if (!id.startsWith(COLLAB_TOOL_ID_PREFIX)) {
    return `${COLLAB_TOOL_ID_PREFIX}${id}` as CollaborationToolId;
  }
  return id as CollaborationToolId;
};

export type CollaborationToolType = string & { __collaborationToolType: symbol };
export const collaborationToolType = (c: string) => c as CollaborationToolType;

export type CollaborationToolDefinition<T extends CollaborationTool = CollaborationTool> =
  {
    constructor: new (...args: any[]) => T;
    fromJSON: (data: CollaborationToolData, ...args: any[]) => T;
  };

export type CollaborationToolMap<T extends CollaborationTool = CollaborationTool> =
  Record<string, CollaborationToolDefinition<T>>;

export type UnknownCollaborationToolData = CollaborationToolData<any>;
export type UnknownCollaborationTool = CollaborationTool<any>;
export const unknownCollaborationToolType = collaborationToolType('unknown');

export type AnyCollaborationToolData = CollaborationToolData<CollaborationToolType>;
export type AnyCollaborationTool = CollaborationTool<CollaborationToolType>;

export const isCollaborationToolData = (data: any): data is CollaborationToolData => {
  return (
    typeof data === 'object' &&
    data.id !== undefined &&
    data.type !== undefined &&
    data.id.startsWith(COLLAB_TOOL_ID_PREFIX)
  );
};

export interface CollaborationToolData<Type extends CollaborationToolType = any> {
  /**
   * The unique identifier of the collaboration tool on the board.
   */
  id: CollaborationToolId;

  /**
   * The type of the collaboration tool on the board.
   */
  type: Type;

  /**
   * The source of the collaboration tool.
   */
  source?: ElementSource;
}

export interface CollaborationTool<
  Type extends CollaborationToolType = any,
> extends CollaborationToolData<Type> {
  /**
   * toJSON() should return a JSON representation of the element
   */
  toJSON(): CollaborationToolData<Type>;
}

export type CollaborationToolState = Partial<Record<string, CollaborationTool>>;
export type CollaborationToolStateData = Partial<Record<string, CollaborationToolData>>;
